import type { V1DraftRequest, V1DraftResponse } from '@aira/shared';
import {
  APP_NAME,
  BUTTON,
  DRAFT_DATA_NOTICE,
  deriveStageStatuses,
  POC_MERCHANT_ID,
  progressToCurrentPhase,
  STATUS,
  type Stage,
} from '@aira/shared';
import { BetaLabel, Stepper } from '@aira/ui';
import {
  Button,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { ArrowLeft, Maximize2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  type DraftHistoryItem,
  fetchDraftHistoryByKey,
  fetchDraftReadiness,
  fetchDraftRunSnapshot,
  getAccessToken,
  markDraftFeedback,
  markDraftInserted,
  type ProgressGroup,
  type ProgressStep,
} from './api/ragBackend';
import { ComposeDraftResult } from './components/ComposeDraftResult';
import { DevBuildVersion } from './components/DevBuildVersion';
import { GroupedProgressList } from './components/GroupedProgressList';
import { PreviousDraftsPanel } from './components/PreviousDraftsPanel';
import { ReplyingToPanel } from './components/ReplyingToPanel';
import { SignIn } from './components/SignIn';
import { StageProgressDetails } from './components/StageProgressDetails';
import { useDraftRequest } from './hooks/useDraftRequest';
import { useEmailContext } from './hooks/useEmailContext';
import { AuthProvider, useAuth } from './lib/auth';
import { getDevBuildLabel } from './lib/devBuildLabel';
import {
  createDialogSession,
  type DialogSession,
  dialogSessionMatchesMessageId,
  readDialogSession,
  removeDialogSession,
  updateDialogSession,
} from './lib/dialogSession';
import { logger } from './lib/logger';
import { getMailboxEmail } from './lib/microsoftAuth';
import {
  closeOutlookTaskPane,
  copyDraftToClipboard,
  displayOutlookReply,
  isOutlookMobile,
} from './lib/officeMailbox';
import {
  clearTaskpaneSnapshot,
  readTaskpaneSnapshot,
  type TaskpaneSnapshot,
  updateTaskpaneSnapshot,
} from './lib/taskpaneSnapshot';
import { OfficeProvider, useOffice } from './providers/OfficeProvider';

type DraftRunResumeState = NonNullable<
  Parameters<ReturnType<typeof useDraftRequest>['attachToRun']>[1]
>;

interface HandedOffRun extends DraftRunResumeState {
  runId: string;
}

function TaskPaneContent() {
  const { context, loading, error: ctxError, reload: reloadEmailContext } = useEmailContext();
  const { signOut, user } = useAuth();
  const {
    state,
    progress,
    groupedProgress,
    currentRunId,
    currentRefinementOperationId,
    isRefining,
    refineError,
    request,
    refine,
    hydrate,
    attachToRun,
    suspend,
    reset,
    regenerateCount,
  } = useDraftRequest();
  const [editedDraft, setEditedDraft] = useState<string | null>(null);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackNoteSaving, setFeedbackNoteSaving] = useState(false);
  const [dialogMsg, setDialogMsg] = useState<string | null>(null);
  // Outlook mobile can't inject a reply from a Read-mode task pane, so the
  // primary action copies the draft to the clipboard instead. Detected once —
  // the host doesn't change under a live pane.
  const [isMobile] = useState(() => isOutlookMobile());
  const [copyNotice, setCopyNotice] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasDialogHandle, setHasDialogHandle] = useState(false);
  const [selectedProgressStage, setSelectedProgressStage] = useState<Stage | null>(null);
  const [historyState, setHistoryState] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    items: DraftHistoryItem[];
    // The messageId this result belongs to. A message switch re-renders with
    // the new messageId before this state catches up (refreshHistory's first
    // setHistoryState call lands in a later effect, not this render), so
    // consumers that must not act on a stale result — like the mobile
    // auto-generate effect below — compare this against context.messageId
    // rather than trusting historyState.status/items alone.
    messageId: string | null;
  }>({ status: 'idle', items: [], messageId: null });
  const [readiness, setReadiness] = useState<{
    status: 'checking' | 'ready' | 'blocked' | 'error';
    reasonCodes: string[];
    message?: string;
  }>({ status: 'checking', reasonCodes: [] });
  const dialogRef = useRef<Office.Dialog | null>(null);
  const dialogOpeningRef = useRef(false);
  const readinessSeqRef = useRef(0);
  const dialogSeedIdRef = useRef<string | null>(null);
  const dialogInsertHandledRef = useRef(false);
  const restoredMessageIdRef = useRef<string | null>(null);
  // True only until the pane's first item has been processed. A genuine
  // Outlook recycle (e.g. Dialog steals the webview) fully remounts this
  // component, so this starts true again on that mount too — which is
  // exactly the case the restore effect below should auto-recover for. A
  // live in-session switch to a *different* thread does not remount, so
  // this is already false by then, and that thread's own saved draft (if
  // any) stays opt-in via the Previous Drafts panel instead of popping back
  // up unannounced.
  const isFirstItemThisMountRef = useRef(true);
  const activeMessageIdRef = useRef<string | null>(null);
  // Track work handed to the dialog so a native close can resume it in the
  // taskpane even when the Office message or localStorage write is dropped.
  const handedOffRunRef = useRef<HandedOffRun | null>(null);
  const resumeHandedOffRun = useCallback(() => {
    const handedOff = handedOffRunRef.current;
    handedOffRunRef.current = null;
    if (!handedOff) return false;
    const { runId, ...resumeState } = handedOff;
    void attachToRun(runId, resumeState);
    return true;
  }, [attachToRun]);

  const updateDialogPresence = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);
      updateTaskpaneSnapshot(context?.messageId, {
        dialogOpen: open,
        dialogSeedId: open ? (dialogSeedIdRef.current ?? undefined) : undefined,
      });
    },
    [context?.messageId],
  );

  const refreshReadiness = useCallback(async (opts?: { silent?: boolean }) => {
    const seq = ++readinessSeqRef.current;
    if (!opts?.silent) setReadiness({ status: 'checking', reasonCodes: [] });
    const delays = [0, 900, 1800];
    try {
      let probe = null as Awaited<ReturnType<typeof fetchDraftReadiness>> | null;
      let lastError: unknown = null;
      for (let i = 0; i < delays.length; i += 1) {
        if (delays[i] > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, delays[i]));
        }
        try {
          probe = await fetchDraftReadiness(10_000, i === delays.length - 1);
          break;
        } catch (err) {
          lastError = err;
        }
      }
      if (!probe) throw lastError ?? new Error('Readiness probe failed');
      if (seq !== readinessSeqRef.current) return;
      setReadiness({
        status: probe.draftReady ? 'ready' : 'blocked',
        reasonCodes: probe.reasonCodes,
        message: probe.message,
      });
    } catch (err) {
      if (seq !== readinessSeqRef.current) return;
      logger.warn('[readiness] probe failed', { err });
      if (opts?.silent) return;
      setReadiness({ status: 'error', reasonCodes: [] });
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    void refreshReadiness();
  }, [user, refreshReadiness]);

  useEffect(() => {
    if (!user) return;
    const refreshIfVisible = () => {
      // Opening/closing an Office Dialog changes focus and visibility. Do not
      // treat that host transition as a reason to refresh the taskpane page.
      if (!isDialogOpen && document.visibilityState === 'visible') {
        void refreshReadiness({ silent: readiness.status === 'ready' });
      }
    };
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [user, readiness.status, refreshReadiness, isDialogOpen]);

  const refreshHistory = useCallback(async () => {
    const messageId = context?.messageId ?? null;
    if (!messageId || readiness.status !== 'ready') {
      setHistoryState({ status: 'idle', items: [], messageId });
      return;
    }
    setHistoryState((prev) => ({ status: 'loading', items: prev.items, messageId }));
    try {
      const items = await fetchDraftHistoryByKey(messageId);
      setHistoryState({ status: 'ready', items, messageId });
    } catch (err) {
      logger.warn('[history] lookup failed', { err });
      setHistoryState({ status: 'error', items: [], messageId });
    }
  }, [context?.messageId, readiness.status]);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  const activeResponse = 'response' in state ? state.response : null;

  const recoverTaskpaneWork = useCallback(
    (snapshot: TaskpaneSnapshot): boolean => {
      if (snapshot.runId) {
        void attachToRun(snapshot.runId, {
          progress: snapshot.progress,
          groupedProgress: snapshot.groupedProgress,
          regenerateCount: snapshot.regenerateCount,
          operationId: snapshot.refinementOperationId,
          preservedResponse: snapshot.response
            ? {
                ...snapshot.response,
                draft: snapshot.editedDraft ?? snapshot.response.draft,
              }
            : undefined,
        });
        return true;
      }
      if (!snapshot.response) return false;
      hydrate(
        snapshot.response,
        snapshot.progress,
        snapshot.groupedProgress,
        snapshot.regenerateCount,
      );
      setEditedDraft(snapshot.editedDraft ?? snapshot.response.draft);
      setFeedback(snapshot.feedback ?? snapshot.response.draft_feedback ?? null);
      return true;
    },
    [attachToRun, hydrate],
  );

  // Outlook may recycle the taskpane webview while a Dialog window has focus.
  // Restore the last result (or resume the popup's generation run) instead of
  // returning the user to the initial Generate screen when the taskpane
  // reloads. Gated to the pane's first item this mount (isFirstItem below) —
  // a genuine recycle remounts the component fresh, so it's still "first
  // item" from this instance's perspective. Without that gate, this fired on
  // every live in-session thread switch too: any thread with a prior saved
  // draft would silently pop the old result screen back up right after the
  // reset-on-item-change effect had just cleared it, even though nothing
  // recycled — confirmed via live testing.
  useEffect(() => {
    const messageId = context?.messageId;
    if (!messageId || state.phase !== 'idle' || restoredMessageIdRef.current === messageId) return;
    restoredMessageIdRef.current = messageId;
    const isFirstItem = isFirstItemThisMountRef.current;
    isFirstItemThisMountRef.current = false;
    if (!isFirstItem) return;
    const snapshot = readTaskpaneSnapshot(messageId);
    if (!snapshot) return;
    if (snapshot.dialogOpen && snapshot.dialogSeedId) {
      dialogSeedIdRef.current = snapshot.dialogSeedId;
      setIsDialogOpen(true);
      // The popup owns its run until its persisted session is marked closed.
      // A recycled taskpane must not start a second poll for that same run.
      return;
    }
    recoverTaskpaneWork(snapshot);
  }, [context?.messageId, state.phase, recoverTaskpaneWork]);

  // The Shared Runtime keeps this task pane alive across message selection —
  // Office does not remount it — so switching to a different email does not
  // reset any of this component's state on its own. Drop the previous item's
  // live workflow view (not its saved snapshot) so the pane reflects the
  // newly selected email instead of staying stuck on the old one; the restore
  // effect above then recovers this item's own snapshot, if any, since phase
  // is now idle.
  useEffect(() => {
    const messageId = context?.messageId;
    if (!messageId || activeMessageIdRef.current === messageId) return;
    const isFirstItem = activeMessageIdRef.current === null;
    activeMessageIdRef.current = messageId;
    if (isFirstItem || isDialogOpen) return;
    setEditedDraft(null);
    setFeedback(null);
    setFeedbackError(null);
    setFeedbackSaving(false);
    setSelectedProgressStage(null);
    setDialogMsg(null);
    setCopyNotice(null);
    reset();
  }, [context?.messageId, isDialogOpen, reset]);

  useEffect(() => {
    const messageId = context?.messageId;
    if (!messageId || !activeResponse) return;
    updateTaskpaneSnapshot(messageId, {
      response: {
        ...activeResponse,
        draft: editedDraft ?? activeResponse.draft,
        draft_feedback: feedback ?? activeResponse.draft_feedback ?? null,
      },
      editedDraft: editedDraft ?? activeResponse.draft,
      feedback,
      progress,
      groupedProgress,
      regenerateCount: regenerateCount.current,
      runId: currentRunId ?? undefined,
      refinementOperationId: currentRefinementOperationId ?? undefined,
    });
  }, [
    context?.messageId,
    activeResponse,
    editedDraft,
    feedback,
    progress,
    groupedProgress,
    currentRunId,
    currentRefinementOperationId,
    regenerateCount.current,
  ]);

  useEffect(() => {
    const messageId = context?.messageId;
    if (!messageId || !currentRunId) return;
    updateTaskpaneSnapshot(messageId, {
      runId: currentRunId,
      refinementOperationId: currentRefinementOperationId ?? undefined,
      progress,
      groupedProgress,
      regenerateCount: regenerateCount.current,
    });
  }, [
    context?.messageId,
    currentRunId,
    currentRefinementOperationId,
    progress,
    groupedProgress,
    regenerateCount.current,
  ]);

  const readinessBlockCopy = (() => {
    if (readiness.message) {
      return {
        title: 'Outlook pilot access is not enabled',
        body: readiness.message,
      };
    }
    if (
      readiness.reasonCodes.includes('microsoft_tenant_not_allowed') ||
      readiness.reasonCodes.includes('microsoft_domain_not_allowed') ||
      readiness.reasonCodes.includes('microsoft_account_not_allowed')
    ) {
      return {
        title: 'Outlook pilot access is not enabled',
        body: 'This Microsoft account is not in the approved AIRA Outlook pilot allowlist.',
      };
    }
    return {
      title: 'AIRA is not activated yet',
      body: STATUS.activationRequired,
    };
  })();

  useEffect(() => {
    if (activeResponse) {
      // A refinement returns a new response while the editor still contains the
      // previous draft. Replace it only when the response changes; subsequent
      // user edits remain local until another result arrives.
      setEditedDraft(activeResponse.draft);
      setFeedback(activeResponse.draft_feedback ?? null);
      setFeedbackError(null);
      setFeedbackSaving(false);
      // A refined/new draft supersedes any prior "copied" confirmation.
      setCopyNotice(null);
    }
  }, [activeResponse]);

  // Stepper view-model — driven by real backend phases via the shared mapper
  // (the ThinkingPanel continues to show the detailed checklist beneath).
  const stepperStages = deriveStageStatuses(
    progressToCurrentPhase(progress),
    state.phase === 'loading'
      ? 'running'
      : state.phase === 'error'
        ? 'failed'
        : state.phase === 'idle'
          ? null
          : 'completed',
  );

  const buildReq = useCallback(
    (): V1DraftRequest => ({
      emailBody: context?.body ?? '',
      emailSubject: context?.subject ?? '',
      senderEmail: context?.senderEmail ?? undefined,
      senderName: context?.senderName ?? undefined,
      emailMessageId: context?.messageId,
      emailReceivedAt: context?.receivedAt,
      forceNew: true,
      merchantId: POC_MERCHANT_ID,
      platform: 'outlook',
      channel: 'email',
      extraInstructions: extraInstructions.trim() || undefined,
    }),
    [context, extraInstructions],
  );

  const handleAnalyze = useCallback(() => {
    if (!context) return;
    if (readiness.status === 'blocked') return;
    clearTaskpaneSnapshot(context.messageId);
    setFeedback(null);
    setFeedbackError(null);
    setFeedbackSaving(false);
    setSelectedProgressStage(null);
    setEditedDraft(null);
    const req = buildReq();
    request(req);
  }, [context, readiness.status, buildReq, request]);

  // True while we don't yet know whether THIS message has a previous draft —
  // either the history lookup hasn't resolved yet, or it resolved for a
  // message we've since switched away from (a message switch re-renders with
  // the new messageId before refreshHistory's own effect updates
  // historyState). On mobile, the idle screen renders a plain loading state
  // instead of the real "Generate Draft" button while this is true, so the
  // button never flashes on screen right before the auto-generate effect
  // below fires and replaces it with the loading phase.
  const mobileHistoryPending =
    isMobile &&
    !!context?.messageId &&
    (historyState.messageId !== context.messageId ||
      historyState.status === 'idle' ||
      historyState.status === 'loading');

  // On Outlook Mobile, the idle "Generate Draft" screen is redundant when
  // there's no previous draft to recover — jump straight to generating, as if
  // the user had already tapped the button. Gate on the history lookup having
  // actually settled for THIS message so a real previous draft isn't raced.
  // One-shot per message via a ref so this doesn't refire after "Start Over"
  // or a completed run brings the phase back to 'idle'.
  const autoGeneratedMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isMobile) return;
    if (!context?.messageId) return;
    if (state.phase !== 'idle') return;
    if (readiness.status !== 'ready') return;
    if (mobileHistoryPending) return;
    if (historyState.status !== 'ready' || historyState.items.length > 0) return;
    if (autoGeneratedMessageIdRef.current === context.messageId) return;
    autoGeneratedMessageIdRef.current = context.messageId;
    handleAnalyze();
  }, [
    isMobile,
    context?.messageId,
    state.phase,
    readiness.status,
    historyState,
    handleAnalyze,
    mobileHistoryPending,
  ]);

  const handleRecoverDraft = async (runId: string) => {
    if (!context) return;
    setFeedback(null);
    setFeedbackError(null);
    setFeedbackSaving(false);
    setSelectedProgressStage(null);
    setEditedDraft(null);
    try {
      const {
        response,
        progress: recoveredProgress,
        groupedProgress: recoveredGroups,
      } = await fetchDraftRunSnapshot(runId);
      setEditedDraft(response.draft);
      setFeedback(response.draft_feedback ?? null);
      hydrate(response, recoveredProgress, recoveredGroups);
    } catch (err) {
      setDialogMsg(`Could not recover that draft: ${String(err)}`);
    }
  };

  const insertReply = useCallback((draftText: string, runId?: string) => {
    // Record that this draft was used (parity with the Gmail add-on).
    if (runId) {
      void markDraftInserted(runId).catch((err) => logger.warn('[mark-inserted] failed', { err }));
    }
    if (displayOutlookReply(draftText)) return;
    // Outlook mobile can't open a reply form from a Read-mode task pane. "Copy &
    // Close" copies the draft to the clipboard and closes the AIRA task pane so
    // the user can open their reply and paste (instructions live in the always-on
    // helper text under the button). If the copy fails, keep the pane open and
    // show the manual-copy fallback instead of closing on nothing.
    if (isOutlookMobile()) {
      void copyDraftToClipboard(draftText).then((copied) => {
        if (copied) {
          closeOutlookTaskPane();
        } else {
          setCopyNotice(STATUS.draftCopyManual);
        }
      });
      return;
    }
    // Log only the length — the draft body is customer-facing content (redacted
    // by scrub anyway via the draft_body key, but don't ship it to the console).
    logger.info('[Preview] Would insert reply', { draftLength: draftText.length });
  }, []);

  const handleInsertReply = (draftText: string, runIdOverride?: string) => {
    const runId = runIdOverride ?? ('response' in state ? state.response.trace_id : undefined);
    insertReply(draftText, runId);
  };

  const insertDialogReplyOnce = useCallback(
    (response: V1DraftResponse) => {
      if (dialogInsertHandledRef.current) return;
      dialogInsertHandledRef.current = true;
      // The Office message and DialogEventReceived can be delivered in either
      // order. Consume the durable request before opening the reply form so a
      // close-session recovery cannot repeat the same insert.
      updateDialogSession(dialogSeedIdRef.current, { insert_requested: false });
      // displayOutlookReply() always targets whichever email is currently open
      // in the host Outlook window — the popup is a separate window, so the
      // user can click through to a different email while it drafts. Inserting
      // here without checking would silently attach this draft to the wrong
      // thread's reply. If the original email is no longer open, leave it for
      // Previous Drafts (the backend already persisted the run) instead of
      // guessing.
      const session = dialogSeedIdRef.current ? readDialogSession(dialogSeedIdRef.current) : null;
      if (!dialogSessionMatchesMessageId(session, context?.messageId)) {
        setDialogMsg(
          'That email is no longer open, so the draft could not be inserted automatically. Reopen it and use Previous Drafts to insert this reply.',
        );
        return;
      }
      insertReply(response.draft, response.trace_id);
    },
    [context?.messageId, insertReply],
  );

  const releaseDialogSession = useCallback(() => {
    removeDialogSession(dialogSeedIdRef.current);
    dialogSeedIdRef.current = null;
    setHasDialogHandle(false);
  }, []);

  const resetDraftState = useCallback(
    (
      preserveDialogPresence: boolean,
      targetMessageId: string | null | undefined = context?.messageId,
    ) => {
      const snapshotMessageId = targetMessageId ?? undefined;
      clearTaskpaneSnapshot(snapshotMessageId);
      if (preserveDialogPresence) {
        updateTaskpaneSnapshot(snapshotMessageId, {
          dialogOpen: true,
          dialogSeedId: dialogSeedIdRef.current ?? undefined,
        });
      }
      setEditedDraft(null);
      setFeedback(null);
      setFeedbackError(null);
      setFeedbackSaving(false);
      setSelectedProgressStage(null);
      reset();
      void refreshHistory();
    },
    [context?.messageId, refreshHistory, reset],
  );

  const handleReset = () => resetDraftState(false);

  const recoverDialogSession = useCallback(
    (session: DialogSession): boolean => {
      // The session's own message_id (recorded when the dialog was opened) is
      // the source of truth for which email this work belongs to — context
      // may have moved on to a different email while the popup was open.
      // Persist saved work under that original messageId so it surfaces via
      // Previous Drafts / the restore-on-return effect later, but only mirror
      // it into the live view (and only auto-attach/auto-insert) when the
      // user is still actually looking at that same email right now.
      const snapshotMessageId = session.message_id ?? context?.messageId;
      const isSameEmail = dialogSessionMatchesMessageId(session, context?.messageId);
      if (session.run_id) {
        handedOffRunRef.current = null;
        updateTaskpaneSnapshot(snapshotMessageId, {
          runId: session.run_id,
          refinementOperationId: session.operation_id ?? undefined,
          dialogOpen: false,
          dialogSeedId: undefined,
        });
        if (isSameEmail) {
          void attachToRun(session.run_id, {
            progress: session.progress,
            groupedProgress: session.grouped_progress,
            regenerateCount: session.regenerate_count,
            operationId: session.operation_id ?? undefined,
            preservedResponse: session.response ?? undefined,
          });
        }
        return true;
      }
      if (session.response) {
        if (isSameEmail) {
          hydrate(
            session.response,
            session.progress,
            session.grouped_progress,
            session.regenerate_count,
            session.refine_error,
          );
          setEditedDraft(session.response.draft);
          setFeedback(session.response.draft_feedback ?? null);
          if (session.insert_requested) {
            insertDialogReplyOnce(session.response);
          }
        } else {
          setDialogMsg(
            'This draft was generated for a different email, which is no longer open. Reopen that email to find it under Previous Drafts.',
          );
        }
        handedOffRunRef.current = null;
        updateTaskpaneSnapshot(snapshotMessageId, {
          response: session.response,
          editedDraft: session.response.draft,
          feedback: session.response.draft_feedback ?? null,
          progress: session.progress,
          groupedProgress: session.grouped_progress,
          regenerateCount: session.regenerate_count,
          runId: undefined,
          refinementOperationId: undefined,
          dialogOpen: false,
          dialogSeedId: undefined,
        });
        return true;
      }
      return false;
    },
    [attachToRun, context?.messageId, hydrate, insertDialogReplyOnce],
  );

  const recoverDetachedDialog = useCallback(() => {
    const seedId = dialogSeedIdRef.current;
    const session = seedId ? readDialogSession(seedId) : null;
    const snapshot = context?.messageId ? readTaskpaneSnapshot(context.messageId) : null;
    if (
      !(session && recoverDialogSession(session)) &&
      !(snapshot && recoverTaskpaneWork(snapshot))
    ) {
      resetDraftState(false);
    }
    setIsDialogOpen(false);
    releaseDialogSession();
  }, [
    context?.messageId,
    recoverDialogSession,
    recoverTaskpaneWork,
    releaseDialogSession,
    resetDraftState,
  ]);

  // A recycled taskpane cannot reacquire the original Office.Dialog object.
  // Recover the popup's persisted final state once that window marks its
  // cross-window session closed, then safely unlock this taskpane.
  useEffect(() => {
    if (!isDialogOpen || hasDialogHandle || !dialogSeedIdRef.current) return;
    const seedId = dialogSeedIdRef.current;
    const recoverClosedSession = () => {
      if (dialogRef.current) return;
      const session = readDialogSession(seedId);
      // A missing session (expired/malformed/removed) has nothing left to
      // recover from — treat it the same as `closed: true` rather than
      // leaving isDialogOpen stuck true forever, which would silently
      // disable every future thread-switch reset for the rest of the
      // Shared Runtime session.
      if (session && !session.closed) return;
      recoverDetachedDialog();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key?.endsWith(seedId)) recoverClosedSession();
    };
    recoverClosedSession();
    const interval = window.setInterval(recoverClosedSession, 500);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
    };
  }, [isDialogOpen, hasDialogHandle, recoverDetachedDialog]);

  // Open the popup composer. The dialog window cannot read Office.context
  // .mailbox.item, so we seed it with the current email context via URL
  // params. If the taskpane has a draft run in flight, we also pass run_id so
  // the dialog attaches to the same backend run instead of starting a second
  // one. The taskpane is locked behind an overlay while the dialog is open
  // and its own poll is suspended; the dialog posts the final draft back via
  // Office.context.ui.messageParent and we hydrate the taskpane state.
  const handleDialogMessage = (raw: string) => {
    let parsed:
      | {
          kind: 'draft-result' | 'insert-draft' | 'state-sync';
          payload: V1DraftResponse;
          progress?: ProgressStep[];
          groupedProgress?: ProgressGroup[];
          regenerateCount?: number;
          refineError?: string | null;
        }
      | { kind: 'run-started'; runId: string; resume?: DraftRunResumeState }
      | { kind: 'start-over' }
      | { kind: 'cancel' }
      | { kind: 'ready' }
      | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Malformed message — fall through to close.
    }

    // The dialog window has no Firebase session; when it signals readiness, hand
    // it a fresh ID token over the messaging channel (never via the URL). This is
    // the auth handshake — do NOT close the dialog.
    if (parsed?.kind === 'ready') {
      void getAccessToken()
        .then((token) =>
          dialogRef.current?.messageChild(
            JSON.stringify({ kind: 'auth', token, mailboxEmail: getMailboxEmail() }),
          ),
        )
        .catch(() => {
          /* parent lost its session — the dialog surfaces NotAuthenticated */
        });
      return;
    }

    // The dialog is a separate window, so the user can keep clicking through
    // emails in the main Outlook window while it drafts. Everything below is
    // scoped to the email the dialog was actually opened for (its session's
    // durable message_id), not whatever context happens to be live right now.
    const dialogSession = dialogSeedIdRef.current
      ? readDialogSession(dialogSeedIdRef.current)
      : null;
    const dialogOwnsCurrentEmail = dialogSessionMatchesMessageId(dialogSession, context?.messageId);
    const dialogMessageId = dialogSession?.message_id ?? context?.messageId;

    if (parsed?.kind === 'run-started') {
      // A run created inside the popup survives the popup window. Retain its id
      // so DialogEventReceived (native X) can resume polling in the task pane.
      handedOffRunRef.current = { runId: parsed.runId, ...parsed.resume };
      updateTaskpaneSnapshot(dialogMessageId, {
        runId: parsed.runId,
        refinementOperationId: parsed.resume?.operationId,
      });
      return;
    }

    if (parsed?.kind === 'start-over') {
      handedOffRunRef.current = null;
      resetDraftState(true, dialogMessageId);
      return;
    }

    if ((parsed?.kind === 'draft-result' || parsed?.kind === 'insert-draft') && parsed.payload) {
      if (dialogOwnsCurrentEmail) {
        hydrate(
          parsed.payload,
          parsed.progress,
          parsed.groupedProgress,
          parsed.regenerateCount,
          parsed.refineError,
        );
        setEditedDraft(parsed.payload.draft);
        setFeedback(parsed.payload.draft_feedback ?? null);
      } else {
        setDialogMsg(
          'This draft was generated for a different email, which is no longer open. Reopen that email to find it under Previous Drafts.',
        );
      }
      handedOffRunRef.current = null;
      if (parsed.kind === 'insert-draft') {
        insertDialogReplyOnce(parsed.payload);
      }
    } else if (parsed?.kind === 'state-sync' && parsed.payload) {
      // Keep the covered task pane hydrated while the popup is active. This is
      // especially important when the user closes the native dialog window X,
      // which emits no final message of its own. Skip it once the user has
      // switched away — the run is still persisted server-side and recoverable
      // via Previous Drafts once they're back on the original email.
      if (dialogOwnsCurrentEmail) {
        hydrate(
          parsed.payload,
          parsed.progress,
          parsed.groupedProgress,
          parsed.regenerateCount,
          parsed.refineError,
        );
        setEditedDraft(parsed.payload.draft);
        setFeedback(parsed.payload.draft_feedback ?? null);
      }
      handedOffRunRef.current = null;
      return;
    } else if (parsed?.kind === 'cancel') {
      // The final Office message can arrive after the popup has persisted a
      // newer response or an in-flight refinement. Recover that durable state
      // before falling back to the process-local handoff ref.
      const session = dialogSeedIdRef.current ? readDialogSession(dialogSeedIdRef.current) : null;
      const recovered = session ? recoverDialogSession(session) : false;
      if (!recovered) resumeHandedOffRun();
    }
    updateDialogPresence(false);
    dialogRef.current?.close();
    dialogRef.current = null;
    releaseDialogSession();
  };

  const closeDialog = () => {
    const session = dialogSeedIdRef.current ? readDialogSession(dialogSeedIdRef.current) : null;
    const recovered = session ? recoverDialogSession(session) : false;
    if (dialogRef.current) {
      try {
        dialogRef.current.close();
      } catch {
        /* host may have closed it already */
      }
      dialogRef.current = null;
    }
    updateDialogPresence(false);
    releaseDialogSession();
    // If the user closed via overlay, treat as cancel — resume the in-flight
    // run in the taskpane if one was handed off.
    if (!recovered) resumeHandedOffRun();
  };

  const openLargerWindow = () => {
    if (isDialogOpen || dialogOpeningRef.current) return;
    if (readiness.status === 'blocked') {
      setDialogMsg(readinessBlockCopy.body);
      return;
    }
    if (typeof Office === 'undefined' || !Office.context?.ui?.displayDialogAsync) {
      setDialogMsg('Dialog API is unavailable on this host.');
      return;
    }
    const seedId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    dialogInsertHandledRef.current = false;
    const subject = context?.subject ?? '';
    const body = context?.body ?? '';
    const from = context?.senderEmail ?? '';
    const storedSnapshot = context?.messageId ? readTaskpaneSnapshot(context.messageId) : null;
    const preservedResponse =
      'response' in state
        ? {
            ...state.response,
            draft: editedDraft ?? state.response.draft,
            draft_feedback: feedback ?? state.response.draft_feedback ?? null,
          }
        : currentRefinementOperationId
          ? (storedSnapshot?.response ?? null)
          : null;
    createDialogSession(seedId, {
      subject,
      body,
      from,
      name: context?.senderName ?? '',
      instructions: extraInstructions,
      message_id: context?.messageId ?? null,
      received_at: context?.receivedAt ?? null,
      run_id: state.phase === 'loading' ? currentRunId : null,
      operation_id: state.phase === 'loading' ? (currentRefinementOperationId ?? null) : null,
      refine_error: refineError,
      insert_requested: false,
      response: preservedResponse,
      progress,
      grouped_progress: groupedProgress,
      regenerate_count: regenerateCount.current,
      closed: false,
    });
    dialogSeedIdRef.current = seedId;
    const params = new URLSearchParams({
      seed_id: seedId,
      subject: subject.slice(0, 500),
      body: body.slice(0, 500),
      from,
      name: context?.senderName ?? '',
      instructions: extraInstructions,
    });
    if (context?.messageId) params.set('message_id', context.messageId);
    if (context?.receivedAt) params.set('received_at', context.receivedAt);
    // If a draft run is already in flight, hand its runId to the dialog so
    // the popup picks up exactly where the taskpane was — same stepper, same
    // backend run, no duplicate work — then suspend the taskpane's poll so
    // the two surfaces don't race for /events.
    if (state.phase === 'loading' && currentRunId) {
      params.set('run_id', currentRunId);
      if (currentRefinementOperationId) {
        params.set('operation_id', currentRefinementOperationId);
      }
      handedOffRunRef.current = {
        runId: currentRunId,
        operationId: currentRefinementOperationId ?? undefined,
        progress,
        groupedProgress,
        regenerateCount: regenerateCount.current,
        preservedResponse: preservedResponse ?? undefined,
      };
      updateTaskpaneSnapshot(context?.messageId, {
        runId: currentRunId,
        refinementOperationId: currentRefinementOperationId ?? undefined,
      });
      suspend();
    } else {
      handedOffRunRef.current = null;
    }
    const url = `${window.location.origin}/dialog.html?${params.toString()}`;
    dialogOpeningRef.current = true;
    const failDialogOpen = (message: string) => {
      dialogOpeningRef.current = false;
      updateDialogPresence(false);
      setDialogMsg(message);
      releaseDialogSession();
      resumeHandedOffRun();
    };
    const onDialogResult = (res: Office.AsyncResult<Office.Dialog>) => {
      dialogOpeningRef.current = false;
      if (res.status !== Office.AsyncResultStatus.Succeeded) {
        failDialogOpen(
          `Dialog API failed on this host — code ${res.error?.code}: ${res.error?.message}`,
        );
        return;
      }
      setDialogMsg(null);
      updateDialogPresence(true);
      const dlg = res.value;
      dialogRef.current = dlg;
      setHasDialogHandle(true);
      dlg.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        const message = (arg as { message?: string }).message ?? '';
        handleDialogMessage(message);
      });
      dlg.addEventHandler(Office.EventType.DialogEventReceived, () => {
        // User closed via the window X, or host raised an error. Recover the
        // persisted popup state first because its last Office message may
        // have been dropped; fall back to the handed-off run only when the
        // session has no terminal response or run of its own.
        const session = dialogSeedIdRef.current ? readDialogSession(dialogSeedIdRef.current) : null;
        const recovered = session ? recoverDialogSession(session) : false;
        dialogRef.current = null;
        updateDialogPresence(false);
        releaseDialogSession();
        if (!recovered) resumeHandedOffRun();
      });
    };
    try {
      Office.context.ui.displayDialogAsync(
        url,
        { height: 70, width: 60, promptBeforeOpen: false },
        onDialogResult,
      );
    } catch (err) {
      failDialogOpen(`Dialog API failed on this host: ${String(err)}`);
    }
  };

  const handleRefineDraft = (note: string) => {
    if (!('response' in state)) return;
    setSelectedProgressStage(null);
    void refine(note, state.response, editedDraft ?? state.response.draft).catch(() => {
      // The hook preserves the prior draft and surfaces the refinement error.
    });
  };

  const sendFeedback = async (action: 'liked' | 'disliked') => {
    if (!('response' in state)) return;
    const fb = action === 'liked' ? 'like' : 'dislike';
    setFeedback(fb);
    setFeedbackError(null);
    setFeedbackSaving(true);
    const runId = state.response.trace_id;
    if (runId) {
      try {
        await markDraftFeedback(runId, fb);
      } catch (err) {
        setFeedbackError('Feedback could not be saved. Please try again.');
        logger.warn('[feedback] persist failed', { err });
      } finally {
        setFeedbackSaving(false);
      }
    } else {
      setFeedbackSaving(false);
    }
  };

  /** Re-send the thumb plus optional free-text note (saved to staff_training_notes). */
  const sendFeedbackNote = async (note: string) => {
    if (!('response' in state) || !feedback) return;
    setFeedbackError(null);
    setFeedbackNoteSaving(true);
    const runId = state.response.trace_id;
    if (runId) {
      try {
        await markDraftFeedback(runId, feedback, { note });
      } catch (err) {
        setFeedbackError('Feedback could not be saved. Please try again.');
        logger.warn('[feedback note] persist failed', { err });
      } finally {
        setFeedbackNoteSaving(false);
      }
    } else {
      setFeedbackNoteSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-3">
        <Spinner label="Loading email context..." />
      </div>
    );
  }

  if (ctxError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{ctxError}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={`relative flex min-h-screen flex-col p-4 ${getDevBuildLabel() ? 'pb-8' : ''}`}>
      {isDialogOpen && (
        <div className="absolute inset-0 z-50 bg-sf-dark/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg p-5 max-w-xs text-center flex flex-col gap-3">
            <p className="text-sm font-semibold text-sf-primary">Continue in the popup window</p>
            <p className="text-xs text-sf-gray">
              The composer is open in a larger window. Generate the draft there and it will appear
              here when you're done.
            </p>
            {hasDialogHandle ? (
              <Button appearance="primary" size="small" onClick={closeDialog}>
                Close popup
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-sf-primary">
                  Close the larger window first. If Outlook does not reconnect automatically, resume
                  here.
                </p>
                <Button appearance="primary" size="small" onClick={recoverDetachedDialog}>
                  I closed it — resume here
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <fieldset
        disabled={isDialogOpen}
        className={`flex min-h-0 min-w-0 flex-1 flex-col gap-4 border-0 p-0 ${isDialogOpen ? 'pointer-events-none select-none' : ''}`}
      >
        {/* ── Header ──
            Outlook's own "Apps" chrome already shows an AIRA icon + name above
            this pane, so this row stays a single compact line (icon + name,
            no separate subtitle block) instead of repeating that logo. */}
        <div className="flex min-w-0 flex-nowrap items-center gap-2">
          <img
            src="/aira-logo-circle.svg"
            alt="Sharefox"
            className="h-5 w-5 shrink-0 object-contain"
          />
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-sm font-semibold leading-none text-sf-primary">
              {APP_NAME}
            </span>
            <BetaLabel wordmarkPx={14} style={{ borderRadius: 6 }} />
          </span>
          {/* The popup is an Office Dialog surface built for the desktop/web
              task pane. On Outlook mobile it isn't an appropriate surface (and
              its Insert would hit the same reply-injection no-op), so hide it. */}
          {!isMobile && (
            <Button
              appearance="subtle"
              size="small"
              icon={<Maximize2 className="h-4 w-4" />}
              className="shrink-0 whitespace-nowrap"
              onClick={openLargerWindow}
              disabled={
                readiness.status === 'blocked' || readiness.status === 'checking' || isRefining
              }
            >
              Open in larger window
            </Button>
          )}
        </div>
        {dialogMsg && (
          <MessageBar intent="warning">
            <MessageBarBody>{dialogMsg}</MessageBarBody>
          </MessageBar>
        )}

        {/* ── Idle actions: Generate Draft + extra instructions belong together ── */}
        {state.phase === 'idle' && mobileHistoryPending ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-sf-gray">
            <Spinner size="tiny" />
            <span>Checking for a previous draft…</span>
          </div>
        ) : (
          state.phase === 'idle' && (
            <div className="flex flex-col gap-3">
              {readiness.status === 'blocked' ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <div className="flex flex-col gap-2">
                      <div>
                        <p className="font-semibold">{readinessBlockCopy.title}</p>
                        <p>{readinessBlockCopy.body}</p>
                      </div>
                      <Button size="small" onClick={() => void refreshReadiness()}>
                        {BUTTON.refresh}
                      </Button>
                    </div>
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  {readiness.status === 'error' && (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        Could not verify activation status. Generate will still be checked by the
                        backend.
                      </MessageBarBody>
                    </MessageBar>
                  )}
                  <p className="text-sm font-normal text-sf-body">{DRAFT_DATA_NOTICE}</p>
                  <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={readiness.status === 'checking'}
                    className="flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5 text-sm font-semibold bg-sf-primary text-sf-secondary hover:bg-sf-primary-hover transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {readiness.status === 'checking'
                      ? 'Checking activation…'
                      : BUTTON.generateDraft}
                  </button>
                  <div>
                    <label
                      htmlFor="extra-instructions"
                      className="text-[10px] uppercase tracking-wide text-sf-gray mb-1 block"
                    >
                      Extra instructions (optional)
                    </label>
                    <textarea
                      id="extra-instructions"
                      value={extraInstructions}
                      onChange={(e) => setExtraInstructions(e.target.value)}
                      placeholder='e.g. "Offer a 10% discount" or "Keep it brief"'
                      rows={2}
                      maxLength={500}
                      disabled={readiness.status === 'checking'}
                      className="w-full resize-y rounded-md border border-sf-primary/20 bg-sf-surface px-3 py-2 text-sm text-sf-dark placeholder:text-sf-gray focus:outline-none focus:ring-2 focus:ring-sf-primary disabled:opacity-50"
                    />
                  </div>
                </>
              )}
            </div>
          )
        )}

        {/* Fluent's Divider ships `flex-grow: 1` (meant for horizontal use in a
            flex row) which, inside this flex-column pane, makes the divider
            itself stretch to swallow all leftover vertical space. Pin it. */}
        {state.phase === 'idle' && <Divider style={{ flexGrow: 0 }} />}

        {/* Keep the email context near the actions until a draft is ready. */}
        {context && !('response' in state) && (
          <ReplyingToPanel
            senderName={context.senderName}
            senderEmail={context.senderEmail}
            subject={context.subject}
            body={context.body}
            onRefresh={reloadEmailContext}
          />
        )}

        {state.phase === 'idle' && context?.messageId && (
          <PreviousDraftsPanel
            status={historyState.status}
            items={historyState.items}
            onRefresh={() => void refreshHistory()}
            onRecover={(runId) => void handleRecoverDraft(runId)}
          />
        )}

        {/* ── Loading ── */}
        {state.phase === 'loading' && (
          <div className="flex flex-col gap-3">
            <Stepper
              stages={stepperStages}
              selectedStage={selectedProgressStage}
              onStageClick={(stage) =>
                setSelectedProgressStage((current) =>
                  current === stage.stage ? null : stage.stage,
                )
              }
            />
            {selectedProgressStage && (
              <StageProgressDetails
                stage={selectedProgressStage}
                steps={progress}
                groups={groupedProgress}
                onClose={() => setSelectedProgressStage(null)}
              />
            )}
            <GroupedProgressList groups={groupedProgress} />
            {!isRefining && (
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={handleReset}
              >
                {BUTTON.back}
              </Button>
            )}
          </div>
        )}

        {/* ── Draft result ── */}
        {'response' in state && (
          <Suspense fallback={<Spinner label="Loading draft..." />}>
            {refineError && (
              <MessageBar intent="error">
                <MessageBarBody>{refineError}</MessageBarBody>
              </MessageBar>
            )}
            <ComposeDraftResult
              response={state.response}
              stepperStages={stepperStages}
              progressSteps={progress}
              progressGroups={groupedProgress}
              draftText={editedDraft ?? state.response.draft}
              onDraftChange={setEditedDraft}
              primaryLabel={isMobile ? BUTTON.copyAndClose : undefined}
              primaryHelpText={isMobile ? STATUS.mobileCopyCloseHelp : undefined}
              primaryNotice={copyNotice}
              feedbackBelow={isMobile}
              onPrimary={() => handleInsertReply(editedDraft ?? state.response.draft)}
              onRefine={handleRefineDraft}
              isRefining={isRefining}
              regenerateCount={regenerateCount.current}
              feedback={feedback}
              feedbackSaving={feedbackSaving}
              feedbackError={feedbackError}
              onFeedback={(fb) => void sendFeedback(fb === 'like' ? 'liked' : 'disliked')}
              onFeedbackNote={(note) => void sendFeedbackNote(note)}
              feedbackNoteSaving={feedbackNoteSaving}
              onStartOver={handleReset}
            />
          </Suspense>
        )}

        {/* A completed draft puts its context last, immediately above sign out. */}
        {context && 'response' in state && (
          <ReplyingToPanel
            senderName={context.senderName}
            senderEmail={context.senderEmail}
            subject={context.subject}
            body={context.body}
            onRefresh={reloadEmailContext}
          />
        )}

        {/* ── Error ── */}
        {state.phase === 'error' && (
          <>
            <MessageBar intent="error">
              <MessageBarBody>
                <Text weight="semibold">Error: </Text>
                {state.message}
              </MessageBarBody>
            </MessageBar>
            <Button appearance="subtle" size="small" onClick={handleReset}>
              {BUTTON.startOver}
            </Button>
          </>
        )}

        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <Text size={200} className="truncate text-sf-gray" title={user?.email ?? undefined}>
            {user?.email ? `Signed in as ${user.email}` : ''}
          </Text>
          <Button appearance="subtle" size="small" onClick={() => void signOut()} title="Sign out">
            Sign out
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-sf-primary-light via-sf-surface to-sf-surface">
        <img src="/aira-logo-circle.svg" alt="Sharefox" className="h-12 w-12 object-contain" />
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-sf-primary/20 border-t-sf-primary" />
      </div>
    );
  }
  if (!user) return <SignIn />;
  return <TaskPaneContent />;
}

function BrowserHostGuard() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-sf-primary-light via-sf-surface to-sf-surface p-6">
      <div className="w-full max-w-sm rounded-2xl border border-sf-primary/10 bg-sf-surface p-6 text-center shadow-xl shadow-sf-primary/10">
        <img src="/aira-logo-circle.svg" alt="AIRA" className="mx-auto h-14 w-14 object-contain" />
        <h1 className="mt-4 text-lg font-semibold text-sf-primary">{APP_NAME}</h1>
        <p className="mt-1 text-sm font-medium text-sf-dark">Open AIRA from Outlook</p>
        <p className="mt-2 text-xs leading-relaxed text-sf-gray">
          This page is the Outlook add-in taskpane. Sideload or deploy the add-in, open an email in
          Outlook, then launch AIRA from the message actions.
        </p>
      </div>
    </div>
  );
}

function OutlookHostGate() {
  const { isOfficeHost } = useOffice();
  if (!isOfficeHost) return <BrowserHostGuard />;
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

export function App() {
  return (
    <OfficeProvider>
      <OutlookHostGate />
      <DevBuildVersion />
    </OfficeProvider>
  );
}
